/**
 * XP-CURVE MIGRATION — re-expresses a save's stored XP totals on the CURRENT level curves (sim/level.ts) so a
 * player keeps exactly the level they had, and how far into it they were, when the curves are re-priced.
 *
 * WHY IT EXISTS. A save stores raw totals (`xp`, `fishXp`, `bugXp`) and derives the level from them. Curve 2
 * (Oct 2026) made every level cost far more XP, so an untouched curve-1 total reads as a much LOWER level —
 * an L40 gardener would have woken up at L23 with plants re-locked. The rule, per track:
 *
 *     L, f  = level and fraction-into-level of `xp` on the OLD curve
 *     xp'   = newTotal(L) + floor(f × newCost(L))          (at the cap: newTotal(50) + the surplus, unchanged)
 *
 * `floor` keeps the result strictly inside level L, so the level can never round up into the next one.
 *
 * ONE-TIME, MARKED BY `xpCurve`. A save carries `xpCurve` = the curve its totals are on; absent means curve 1.
 * `migrateXpCurve` is a no-op on a save already at XP_CURVE_VERSION, so it is safe to run on every load.
 *
 * THE SERVER OWNS IT TOO. Every conversion RAISES the stored totals (old-L27 garden: 50,000 → 938,354), far past
 * the save rate limit's burst, so a client-only migration would be clamped back by the next PUT. The same rule
 * is hand-copied in server/index.js (`convertXpTracks`, run inside `sanitise` before the plausibility clamp) and
 * pinned to this file by serverMirrors.test.ts. Change one, change both.
 */
import {
  MAX_LEVEL, xpForLevel, fishXpForLevel, bugXpForLevel, xpForLevelV1, fishXpForLevelV1, bugXpForLevelV1,
} from "./level";

/** The curve the current level functions implement. Bump it (and add a step below) whenever a curve changes. */
export const XP_CURVE_VERSION = 2;

type Curve = (n: number) => number;

/** Re-express one XP total from `from` onto `to`, keeping its level and fraction-into-level. */
export function remapXp(xp: number, from: Curve, to: Curve): number {
  if (!(typeof xp === "number" && Number.isFinite(xp)) || xp <= 0) return xp;
  let level = 1;
  while (level < MAX_LEVEL && xp >= from(level + 1)) level++;
  if (level >= MAX_LEVEL) return to(MAX_LEVEL) + (xp - from(MAX_LEVEL));
  const base = from(level), span = from(level + 1) - base;
  const nBase = to(level), nSpan = to(level + 1) - nBase;
  return nBase + Math.floor(((xp - base) / span) * nSpan);
}

/** The three tracks' curve-1 → curve-2 maps. */
export const XP_TRACKS = [
  { key: "xp", v1: xpForLevelV1, v2: xpForLevel },
  { key: "fishXp", v1: fishXpForLevelV1, v2: fishXpForLevel },
  { key: "bugXp", v1: bugXpForLevelV1, v2: bugXpForLevel },
] as const;
export type XpTrackKey = (typeof XP_TRACKS)[number]["key"];

/** Is a raw save's `xpCurve` stamp the current curve? (Absent / junk = curve 1.) */
export function onCurrentXpCurve(raw: { xpCurve?: unknown } | null | undefined): boolean {
  const v = raw && typeof raw === "object" ? raw.xpCurve : undefined;
  return typeof v === "number" && v >= XP_CURVE_VERSION;
}

/**
 * The save's three XP totals on the CURRENT curve: converted if the save is on curve 1, returned as-is if it is
 * already current. Missing / non-numeric totals pass through untouched (the caller's coercion defaults them).
 * Pure — it never mutates `raw`.
 */
export function migrateXpCurve<T extends { xp?: unknown; fishXp?: unknown; bugXp?: unknown; xpCurve?: unknown }>(
  raw: T,
): T & { xpCurve: number } {
  if (onCurrentXpCurve(raw)) return { ...raw, xpCurve: XP_CURVE_VERSION };
  const out: Record<string, unknown> = { ...raw, xpCurve: XP_CURVE_VERSION };
  for (const t of XP_TRACKS) {
    const v = raw[t.key];
    if (typeof v === "number") out[t.key] = remapXp(v, t.v1, t.v2);
  }
  return out as T & { xpCurve: number };
}
